<template>
  <f-box
    :class="[
      `document-card`,
      `document-card--${size}`,
      {
        'is-minimal': minimal,
      }
    ]"
  >
    <div 
      v-if="innerDocument"
      class="document-card--infos is-grid is-aligned"
    >
      <div class="is-col-1">
        <p 
          class="document-card--name"
        >
          <i 
            :class="[
              `uil`,
              docState,
              docStatus,
            ]"
          ></i>

          <i 
            :class="[
              {
                'uil uil-envelope is-success': innerDocument.email.sent, 
              }
            ]"
          ></i>
        </p>
      </div>

      <div class="is-col-1">
        <p 
          class="document-card--name is-ellipsis"
        >
          <small>
            <b>
              {{ docType }}
            </b>
          </small>
        </p>
      </div>

      <div class="is-col-2 is-overflow-hidden">
        <p 
          class="document-card--name is-ellipsis"
        >
          <small>
            <b>
              {{ innerDocument.numerotation }}
            </b>
          </small>
        </p>
      </div>
      
      <div class="is-col-2 is-overflow-hidden">
        <p 
          class="document-card--siret is-ellipsis"
        >
          <small>
            <b>
              {{ formatDate(innerDocument.date) }}
            </b>
          </small>
        </p>
      </div>

      <div class="is-col-4 is-overflow-hidden">
        <p 
          class="document-card--name is-ellipsis"
        >
          <small>
            <b>
              {{ innerDocument.client.name }}
            </b>
          </small>
        </p>
      </div>

      <div class="is-col-1 is-aligned">
        <f-button
          @click="patchDocument"
          circle
        >
          <i class="uil uil-save"></i>
        </f-button>

         <f-button
          v-if="innerDocument.payment.status === 'submitted'"
          @click="resetStripePayment"
          type="info"
          circle
        >
          <i 
            :class="[
              `uil`,
              {
                'uil-transaction': waitConfirmation !== RESET_STRIPE,
                'uil-shield-question': waitConfirmation === RESET_STRIPE,
              }
            ]"
          ></i>
        </f-button>

        <f-button
          v-if="canDesarchive"
          @click="desarchiveDocument"
          type="info"
          circle
        >
          <i 
            :class="[
              `uil`,
              {
                'uil-file-alt': waitConfirmation !== DESARCHIVE,
                'uil-shield-question': waitConfirmation === DESARCHIVE,
              }
            ]"
          ></i>
        </f-button>

        <f-button
          @click="reeditDocument"
          type="danger"
          circle
        >
          <i 
            :class="[
              `uil`,
              {
                'uil-file-edit-alt': waitConfirmation !== REEDITION,
                'uil-shield-question': waitConfirmation === REEDITION,
              }
            ]"
          ></i>
        </f-button>

        <f-button
          v-if="canRemove"
          @click="removeDocument"
          type="danger"
          circle
        >
          <i 
            :class="[
              `uil`,
              {
                'uil-trash': waitConfirmation !== DELETION,
                'uil-shield-question': waitConfirmation === DELETION,
              }
            ]"
          ></i>
        </f-button>
      </div>
    </div>
  </f-box>
</template>

<script>
  import { ref, computed } from 'vue'
  import { useStore } from '@freebe/sdk/store'
  import { useConstants } from '@freebe/sdk/constants'
  import useDate from '@composables/use-date'
  import useRoles from '@composables/use-roles'
  import router from '@core/router'

  const Constants = useConstants('documents')
  const DESARCHIVE = 'desarchive'
  const REEDITION = 'reedition'
  const DELETION = 'deletion'
  const RESET_STRIPE = 'reset_stripe'

  export default {
    props: {
      document: {
        type: [Object, undefined],
        default: undefined,
      },

      id: {
        type: String,
        default: '',
      },

      selected: {
        type: Boolean,
        default: false,
      },

      selectable: {
        type: Boolean,
        default: false,
      },

      minimal: {
        type: Boolean,
        default: false,
      },

      size: {
        type: String,
        default: 'normal',
      },

      oneShotSelection: {
        type: Boolean,
        default: false,
      },
    },

    setup (props, { emit }) {
      const { get, fetch, patch, remove } = useStore('@freebe/documents')
      const { checkPermissions } = useRoles()

      let innerDocument = ref(props.document || null)
      let documentId = ref(props.id || innerDocument.value._id)

      if (!innerDocument.value && documentId.value) {
        innerDocument = computed(() => get(documentId.value))

        fetch(documentId.value)
      }

      function toggle () {
        emit('toggle', documentId.value, !!props.selected)
      }

      function toggleOneShot () {
        if (props.oneShotSelection && props.selectable) {
          toggle()
        }
      }

      async function patchDocument() {
        const data = innerDocument.value
        const params = {
          query: {
            user: innerDocument.value.user_id
          }
        }
        
        try {
          await patch(innerDocument.value._id, data, params)
        } catch (error) {
          console.error('NOTIFICATION ERROR:', error)
        }
      }

      const mapStates = {
        'pending': 'is-info',
        'paid': 'is-success',
        'late': 'is-danger',
        'partially_paid': 'is-info',
        'cancelled': 'is-blue-dark',
      }

      const mapStatuses = {
        'draft': 'uil-file-alt',
        'verification': 'uil-file-alt',
        'validation': 'uil-file-check',
        'sent': 'uil-file-check',
        'reedition': 'uil-file-alt',
      }

      const mapTypes = {
        'invoices': 'Facture',
        'quotations': 'Devis',
        'credits': 'Avoir',
      }

      const docState = computed(() => {
        return mapStates[innerDocument.value.state] || 'is-disabled'
      })

      const docStatus = computed(() => {
        return mapStatuses[innerDocument.value.status] || 'uil-file-question'
      })

      const docType = computed(() => {
        return mapTypes[innerDocument.value.type] || 'Document'
      })

      const waitConfirmation = ref(false)
      
      const canRemove = computed(() => 
        checkPermissions('freebe/documents:remove') 
        && (innerDocument.value.imported || innerDocument.value.archived)
      )

      const canDesarchive = computed(() => innerDocument.value.archived)

      async function resetStripePayment () {
        if (waitConfirmation.value === RESET_STRIPE) {
          waitConfirmation.value = false

          await patch(documentId.value, {
            'payment.status': 'pending',
            'payment.session_id': '',
          }, {
            query: {
              user_id: innerDocument.value.user_id
            }
          })
        } else {
          waitConfirmation.value = RESET_STRIPE

          setTimeout(() => {
            waitConfirmation.value = false
          }, 2000)
        }
      }

      async function desarchiveDocument () {
        if (waitConfirmation.value === DESARCHIVE) {
          waitConfirmation.value = false

          await patch(documentId.value, {
            archived: false,
          }, {
            query: {
              user_id: innerDocument.value.user_id
            }
          })

          router.push({
            name: 'documents-list',
            params: {
              id: router.currentRoute.value.params.id,
            }
          })
        } else {
          waitConfirmation.value = DESARCHIVE

          setTimeout(() => {
            waitConfirmation.value = false
          }, 2000)
        }
      }

      async function reeditDocument () {
        if (waitConfirmation.value === REEDITION) {
          waitConfirmation.value = false

          await patch(documentId.value, {
            status: Constants.statuses.reedition
          }, {
            query: {
              user_id: innerDocument.value.user_id
            }
          })

          router.push({
            name: 'documents-list',
            params: {
              id: router.currentRoute.value.params.id,
            }
          })
        } else {
          waitConfirmation.value = REEDITION

          setTimeout(() => {
            waitConfirmation.value = false
          }, 2000)
        }
      }

      async function removeDocument () {
        if (waitConfirmation.value === DELETION) {
          waitConfirmation.value = false

          await remove(documentId.value, {
            query: {
              user_id: innerDocument.value.user_id
            }
          })

          router.push({
            name: 'documents-list',
            params: {
              id: router.currentRoute.value.params.id,
            }
          })
        } else {
          waitConfirmation.value = DELETION

          setTimeout(() => {
            waitConfirmation.value = false
          }, 2000)
        }
      }

      return {
        docState,
        docStatus,
        docType,
        innerDocument,
        toggle,
        toggleOneShot,
        canRemove,
        canDesarchive,
        waitConfirmation,
        patchDocument,
        desarchiveDocument,
        resetStripePayment,
        reeditDocument,
        removeDocument,
        REEDITION,
        DELETION,
        DESARCHIVE,
        RESET_STRIPE,
        ...useDate(),
      }
    },
  }
</script>

<style lang="scss">
  .document-card {
    width: 100%;
    height: 100%;

    &--name {
      color: $color-secondary;
      margin: 0;
      padding: 0.5rem 0px;
    }

    & > div {
      height: 100%;
      width: 100%;
    }

    &.is-select-one-shot {
      .document-card--badge,
      .document-card--name {
        cursor: pointer;
      }
    }

    &:hover, &.is-selected {
      &.is-select-one-shot {
        // box-shadow: 0 1px 1px 0 rgba(102, 166, 255, 0.3) !important;
      }

      .document-card--one-shot-toggle {
        transform: translate3d(0, 0, 0);
        opacity: 1;
      }
    }

    &--one-shot-toggle {
      transform: translate3d(-20px, 0, 0);
      opacity: 0;
      transition: opacity 0.1s, transform 0.3s $magic-animation;

      &.f-button {
        box-shadow: unset;
        font-size: 1.5rem;
      }
    }

    &--normal {
      min-height: 4rem;
      padding: 0 1rem;
      margin-bottom: 0.33rem;

      &.is-minimal {
        height: 2.8rem;
      }
    }
  }
</style>